/**
 * collectObsTaggedNotes — gather Observation notes tagged to a Criterion
 * Mapping page (A1–B4, Self-Reported Behaviours, or Other Behaviours).
 *
 * Notes can be authored in two places:
 *   1. Task Administration — per-task `masterNotes` plus General Observations
 *      panels (`general_self_reported.notes`, `general_sensory.notes`).
 *   2. ADOS-informed Coding — per-item `ados_<id>_notes` arrays on adosData.
 *
 * Callers that also re-present the Self-Report box separately (Criterion
 * Mapping Self-Reported page) should filter out `general_self_reported` from
 * the returned `taskNotes` so those notes are not listed twice.
 *
 * Each note carries `criteriaTags: string[]`. This helper filters those arrays
 * and attaches a short source label plus a `sourceRef` so Criterion Mapping
 * can re-tag notes in place (writing back to the same Task Admin / ADOS array).
 *
 * Pure and side-effect free — safe to call inside useMemo.
 */

import { ADOS_ALL_ITEMS } from '../config/adosConfig';
import { isObservationTaskAdministered } from '../../../../utils/observationTaskAdministration';

/**
 * @typedef {object} ObsNoteSourceRef
 * @property {'task'|'ados'} kind
 * @property {string|number} dataKey  Task id, `general_*` key, or ADOS item id
 * @property {string} field           Array field within that entry
 */

/**
 * @typedef {object} TaggedNoteView
 * @property {string|number} id
 * @property {string} text
 * @property {string} [type]
 * @property {string[]} [criteriaTags]
 * @property {string[]} [questions]
 * @property {string[]} [domains]
 * @property {string} sourceLabel  Human-readable origin for the note card header
 * @property {'task'|'ados'} sourceKind
 * @property {ObsNoteSourceRef} sourceRef
 */

/**
 * @param {object} options
 * @param {string} options.tagId  Tag / page id (e.g. 'A1', 'other')
 * @param {Array<number|string>} options.selectedTaskIds
 * @param {Array<{id: number|string, shortTitle: string}>} options.tasks
 * @param {object} options.allTaskData
 * @param {object} [options.adosData]
 * @returns {{ taskNotes: TaggedNoteView[], adosNotes: TaggedNoteView[] }}
 */
export function collectObsTaggedNotes({
  tagId,
  selectedTaskIds,
  tasks,
  allTaskData = {},
  adosData = {},
}) {
  const taskNotes = [];
  const adosNotes = [];
  if (!tagId) return { taskNotes, adosNotes };

  const noteHasTag = (note) => (
    Array.isArray(note?.criteriaTags) && note.criteriaTags.includes(tagId)
  );

  // ── Task Administration notes (administered tasks only) ────────────────
  for (let i = 0; i < (selectedTaskIds || []).length; i += 1) {
    const taskId = selectedTaskIds[i];
    const task = (tasks || []).find(
      (t) => t.id === taskId || String(t.id) === String(taskId),
    );
    const dataKey = task?.id ?? taskId;
    const taskData = allTaskData[taskId] ?? allTaskData[task?.id];
    if (!isObservationTaskAdministered(taskData)) continue;

    const masterNotes = Array.isArray(taskData?.masterNotes) ? taskData.masterNotes : [];
    const sourceLabel = task
      ? `Task ${i + 1}. ${task.shortTitle}`
      : `Task ${i + 1}`;

    for (const note of masterNotes) {
      if (!noteHasTag(note)) continue;
      taskNotes.push({
        ...note,
        sourceLabel,
        sourceKind: 'task',
        sourceRef: { kind: 'task', dataKey, field: 'masterNotes' },
      });
    }
  }

  // General Observations panels also live under allTaskData.
  const generalPanels = [
    { key: 'general_self_reported', label: 'Self-Report' },
    { key: 'general_sensory', label: 'Sensory (B4)' },
  ];
  for (const panel of generalPanels) {
    const panelData = allTaskData[panel.key];
    const notes = Array.isArray(panelData?.notes) ? panelData.notes : [];
    for (const note of notes) {
      if (!noteHasTag(note)) continue;
      taskNotes.push({
        ...note,
        sourceLabel: panel.label,
        sourceKind: 'task',
        sourceRef: { kind: 'task', dataKey: panel.key, field: 'notes' },
      });
    }
  }

  // ── ADOS-informed Coding notes ─────────────────────────────────────────
  for (const item of ADOS_ALL_ITEMS) {
    const field = `ados_${item.id}_notes`;
    const notes = adosData[field];
    if (!Array.isArray(notes)) continue;
    for (const note of notes) {
      if (!noteHasTag(note)) continue;
      adosNotes.push({
        ...note,
        sourceLabel: `ADOS ${item.id}. ${item.title}`,
        sourceKind: 'ados',
        sourceRef: { kind: 'ados', dataKey: item.id, field },
      });
    }
  }

  return { taskNotes, adosNotes };
}
